import React, { useContext, useMemo } from "react";
import InvitesPanel from "../components/invites";
import TemplatesPanel from "../components/templates";
import { AuthContext } from "../auth/AuthContext";
import CompanySettingsPanel from "../components/company_settings";
import { Tab, Tabs } from "@mui/material";
import { EnvVarsPanel } from "./settings/env-vars/envVars";
import DeviceConfigs from "./settings/device-configs";
import { useNavigate, useParams } from "react-router";
import styled from "@emotion/styled";
import { useAtomValue } from "jotai";
import { companyDataAtom } from "./test-run/test-run-atoms";
import IntegrationsConfigs from "./settings/integrations/integrations";
import AudiosPanel from "./settings/audios";
import { ApiKeysPanel } from "./settings/api-keys/api-keys";
import { AppHandbookPanel } from "./settings/handbook/app-handbook";
import UsagePanel from "./settings/usage/usage";
import { useDocumentTitle } from "@/hooks/useDocumentTitle";

interface SettingsTab {
  key: string;
  label: string;
}

const GlobalSettings = () => {
  useDocumentTitle("Settings");
  const { section } = useParams<{ section?: string }>();
  const navigate = useNavigate();
  const { user } = useContext<any>(AuthContext);
  const { data: companyData } = useAtomValue(companyDataAtom(user.companyId));

  const tabs = useMemo<SettingsTab[]>(() => {
    const t: SettingsTab[] = [
      { key: "settings", label: "Settings" },
      { key: "members", label: "Members" },
      { key: "devices", label: "Devices" },
      { key: "env-vars", label: "Env Variables" },
      { key: "api-keys", label: "API Keys" },
      { key: "templates", label: "Templates" },
    ];
    if (companyData?.settings?.enableAudioFiles)
      t.push({ key: "audios", label: "Audios" });
    if (companyData?.settings?.displayIntegrationsSettings)
      t.push({ key: "integrations", label: "Integrations" });
    if (companyData?.settings?.enableAppHandbook)
      t.push({ key: "handbook", label: "Handbook" });
    t.push({ key: "usage", label: "Usage" });
    return t;
  }, [companyData]);

  const activeIndex = Math.max(
    0,
    tabs.findIndex((t) => t.key === (section ?? "settings"))
  );
  const activeKey = tabs[activeIndex]?.key ?? "settings";

  const displayCorrectPanel = (key: string) => {
    switch (key) {
      case "settings":
        return <CompanySettingsPanel companyId={user.companyId} />;
      case "templates":
        return <TemplatesPanel />;
      case "audios":
        return <AudiosPanel />;
      case "members":
        return <InvitesPanel companyId={user.companyId} />;
      case "env-vars":
        return <EnvVarsPanel companyId={user.companyId} />;
      case "api-keys":
        return <ApiKeysPanel companyId={user.companyId} />;
      case "devices":
        return <DeviceConfigs />;
      case "integrations":
        return <IntegrationsConfigs />;
      case "handbook":
        return <AppHandbookPanel companyId={user.companyId} />;
      case "usage":
        return <UsagePanel />;
      default:
        return <CompanySettingsPanel companyId={user.companyId} />;
    }
  };

  return (
    <ContentWrapper>
      <TabsWrapper>
        <Tabs
          value={activeIndex}
          onChange={(_, newValue) => {
            const key = tabs[newValue]?.key;
            navigate(
              key === "settings"
                ? "/gpt-driver/settings"
                : `/gpt-driver/settings/${key}`,
              { replace: true }
            );
          }}
          sx={{
            minHeight: 48,
            "& .MuiTab-root": {
              minHeight: 48,
              textTransform: "none",
              fontWeight: 500,
              fontSize: "1rem",
            },
          }}
        >
          {tabs.map((tab) => (
            <Tab key={tab.key} label={tab.label} />
          ))}
        </Tabs>
      </TabsWrapper>
      <PanelWrapper>{displayCorrectPanel(activeKey)}</PanelWrapper>
    </ContentWrapper>
  );
};

const ContentWrapper = styled.div`
  display: grid;
  grid-template-areas:
    "tabs"
    "settings";
  row-gap: 20px;
  padding: 16px;
  background-color: #e8e8e8;
  grid-template-rows: auto minmax(0, 1fr);
  margin: 16px;
  border-radius: 8px;
  height: calc(100% - 32px);
`;

const TabsWrapper = styled.div`
  grid-area: tabs;
`;

const PanelWrapper = styled.div`
  grid-area: settings;
`;

export default GlobalSettings;
